import { useMemo } from 'react'

import { F0Dialog, F0Form, useF0Form } from '@factorialco/f0-react'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import users from 'modules/core/store/collections/users'

import useMoveUserToPermissionGroup from 'modules/permissions/hooks/useMoveUserToPermissionGroup'
import { useSinglePermissionGroupSelectorFormField } from 'modules/permissions/lib/formFields/permissionGroupSelector'

interface Props {
  userId: string
  onClose: () => void
}

const ChangeGroup = ({ userId, onClose }: Props) => {
  const [moveUser] = useMoveUserToPermissionGroup()
  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()
  const user = useMemo(() => users.get(String(userId)), [userId])

  const permissionGroupField = useSinglePermissionGroupSelectorFormField({
    label: i18n.t('settings.permissions.group.modal.group'),
    mandatory: true,
  })

  const formSchema = useMemo(
    () =>
      z.object({
        permissionGroupId: permissionGroupField,
      }),
    [permissionGroupField]
  )


  if (!user) return null

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('settings.permissions.group.modal.title')}
      description={i18n.t('settings.permissions.group.modal.description')}
      primaryAction={{
        label: i18n.t('settings.permissions.group.modal.submit', {
          user: getFullDisplayName({
            firstName: user.attributes.first_name,
            lastName: user.attributes.last_name,
          }),
        }),
        onClick: submit,
        disabled: hasErrors,
        loading: isSubmitting,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <F0Form
        formRef={formRef}
        name="change-permission-group"
        schema={formSchema}
        defaultValues={{}}
        submitConfig={{ type: 'default', hideSubmitButton: true }}
        onSubmit={async (data) => {
          if (!data.permissionGroupId) return { success: false }

          await moveUser({
            permissionGroupId: data.permissionGroupId,
            accessId: userId,
          })
          onClose()
          return { success: true }
        }}
      />
    </F0Dialog>
  )
}

export default ChangeGroup
